import * as React from "react";
import classNames from "classnames";

import { DateLike } from "~/lib/date";
import LegacyDispatcher from "~/lib/legacyDispatcher";
import JobLogLineStore from "~/stores/JobLogLineStore";
import TestTooltip from "~/components/analytics/shared/Tooltips/TestTooltip";
import { track } from "~/lib/segmentAnalytics";
import {
  LogCopyPermalink,
  LogHoverContext,
  LogLine,
  LogLineContent,
  LogLineNumber,
  LogTimestamp,
} from "~/components/shared/Log";
import { LogLineActionsDropdown } from "~/components/shared/LogLineActionsDropdown";
import { useLogLineSelection } from "~/components/build/Show/lib/useLogLineSelection";
import { FailureMarker } from "~/components/job/Job/Log/useTestFailureMarkers";

interface LogLineSelectionProps {
  selectLine: (jobId: string, lineNumber: number) => void;
  selectRange: (jobId: string, startLine: number, endLine: number) => void;
}

interface Props extends React.HTMLAttributes<HTMLTableRowElement> {
  id: string;
  jobId: string;
  number: number;
  line: string;
  startedAt: DateLike | null | undefined;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC: boolean | null | undefined;
  onTimestampClick: (arg1: React.MouseEvent<HTMLElement>) => void | null | undefined;
  innerRef?: React.Ref<HTMLTableRowElement> | null;
  failureMarker?: FailureMarker;
}

type PropsWithSelection = Props & LogLineSelectionProps;

// When changing to Typescript, this can be imported from 'TestTooltip'
type TestProps = {
  uuid: string;
  activity: {
    activities: Array<{
      createdAt: string;
      duration: number;
      url: string;
      humanDuration: string;
      id: string;
      result: string;
    }>;
    limit: number;
  };
  reliability: string;
  failedCount: number;
  p50Duration: number;
  p95Duration: number;
  url: string;
};

type State = {
  highlighted: boolean;
  highlightedRange: { start: number; end: number } | null;
  test?: TestProps;
  testLoading: boolean;
  showTooltip: boolean;
};

const getStateFromStores = (id: string, jobId: string) => {
  return {
    highlighted: JobLogLineStore.isHighlighted(id),
    highlightedRange: JobLogLineStore.getHighlightedRange(jobId),
  };
};

class JobLogOutputLine extends React.PureComponent<PropsWithSelection, State> {
  static contextType = LogHoverContext;
  declare context: React.ContextType<typeof LogHoverContext>;

  tooltipWrapperNode: HTMLSpanElement | null | undefined;

  constructor(props: PropsWithSelection) {
    super(props);

    this.state = {
      ...getStateFromStores(props.id, props.jobId),
      test: undefined,
      testLoading: false,
      showTooltip: false,
    } as State;
  }

  componentDidMount() {
    JobLogLineStore.addChangeListener(this.props.id, this.handleLineStoreChange);

    // Listen for range changes to update highlighting when range selection changes
    JobLogLineStore.addRangeChangeListener(this.props.jobId, this.handleLineStoreChange);
  }

  componentWillUnmount() {
    JobLogLineStore.removeChangeListener(this.props.id, this.handleLineStoreChange);
    JobLogLineStore.removeRangeChangeListener(this.props.jobId, this.handleLineStoreChange);
  }

  handleLineStoreChange = () => {
    return this.setState(getStateFromStores(this.props.id, this.props.jobId));
  };

  handleClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    const { jobId, selectLine, selectRange } = this.props;
    const lineNumber = this.props.number;

    // Handle shift+click for range selection
    if (Features.LogViewerMultiLineSelection && evt.shiftKey) {
      const anchor = JobLogLineStore.getAnchorLine();

      if (anchor && anchor.jobId === jobId) {
        // Create range from anchor to this line
        const startLine = Math.min(anchor.lineNumber, lineNumber);
        const endLine = Math.max(anchor.lineNumber, lineNumber);

        // Clear hover state before creating range to ensure button shows on first line
        this.context.clearHoveredLine();

        // Update URL - this will trigger the provider to sync to stores
        selectRange(jobId, startLine, endLine);
        return;
      }
    }

    // Regular click: select single line
    // Update URL - this will trigger the provider to sync to stores
    selectLine(jobId, lineNumber);
  };

  handleTimestampClick = (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();

    if (this.props.onTimestampClick) {
      this.props.onTimestampClick(evt);
    }
  };

  handleTooltipLineClick = async (evt: React.MouseEvent<HTMLElement>) => {
    const { target } = evt;

    if (!(target instanceof HTMLAnchorElement) || !target.href) {
      return;
    }

    const testAnalyticsUrlRegex =
      /\/organizations\/([^/]+)\/analytics\/suites\/([^/]+)\/tests\/([^/]+)/;
    const testAnalyticsMatch = target.href.match(testAnalyticsUrlRegex);

    if (testAnalyticsMatch) {
      evt.preventDefault();
      const organizationSlug = testAnalyticsMatch[1];
      this.setState({ showTooltip: true });
      document.documentElement &&
        document.documentElement.addEventListener("click", this.handleDocumentClick);

      _trackTestTooltipClicked({
        organizationSlug: organizationSlug,
      });
    }
  };

  handleDocumentClick = (evt: MouseEvent) => {
    if (!(evt.target instanceof HTMLElement)) {
      return;
    }

    if (this.tooltipWrapperNode && !this.tooltipWrapperNode.contains(evt.target)) {
      this.setState({ showTooltip: false });
      document.documentElement &&
        document.documentElement.removeEventListener("click", this.handleDocumentClick);
    }
  };

  render() {
    const {
      timestampsInUTC,
      id,
      jobId,
      line,
      number,
      onTimestampClick,
      startedAt,
      timestampsOn,
      innerRef,
      className,
      failureMarker,
      style,
      ...props
    } = this.props;

    // Determine if the copy button should be shown and if elevated z-index is needed.
    // The store centralizes this logic for both Line and Header components.
    const showCopyButton = JobLogLineStore.shouldShowCopyButton(
      this.props.jobId,
      this.props.number,
    );

    const copyButton = Features.LogViewerCopyLinesAction ? (
      <LogLineActionsDropdown jobId={this.props.jobId} lineNumber={this.props.number} />
    ) : (
      <LogCopyPermalink
        jobId={this.props.jobId}
        lineNumber={this.props.number}
        label={`Copy link to line ${this.props.number}`}
      />
    );

    const mergedStyle = failureMarker
      ? {
          ...style,
          boxShadow: "inset 4px 0 0 0 var(--theme-error-marker)",
          backgroundColor: "var(--theme-bg-2)",
        }
      : style;

    return (
      <LogLine
        ref={innerRef}
        highlighted={this.state.highlighted}
        // When showing the copy button, use higher z-index so it isn't clipped by subsequent rows
        className={classNames(className, { "!z-[3]": showCopyButton })}
        style={mergedStyle}
        title={failureMarker ? `Failed test: ${failureMarker.failure.testName}` : undefined}
        {...props}
      >
        <LogLineNumber
          number={this.props.number}
          highlighted={this.state.highlighted}
          copyButton={copyButton}
          showCopyButton={showCopyButton}
          jobId={this.props.jobId}
          onClick={this.handleClick}
        />

        {this.props.timestampsOn && (
          <LogTimestamp
            time={this.props.startedAt}
            utc={this.props.timestampsInUTC}
            onClick={this.handleTimestampClick}
          />
        )}

        <LogLineContent colSpan={2}>
          <span
            ref={(tooltipWrapperNode) => (this.tooltipWrapperNode = tooltipWrapperNode)}
            style={{ position: "relative" }}
          >
            <span
              dangerouslySetInnerHTML={{ __html: this.props.line }}
              onClick={this.handleTooltipLineClick}
            />
            {this.state.showTooltip && (
              <TestTooltip
                // @ts-expect-error - TS2322 - Type 'HTMLSpanElement | null | undefined' is not assignable to type 'HTMLElement'.
                wrapperNode={this.tooltipWrapperNode}
              />
            )}
          </span>
        </LogLineContent>
      </LogLine>
    );
  }
}

type TrackingProps = {
  organizationSlug: string;
};

function _trackTestTooltipClicked({ organizationSlug }: TrackingProps) {
  track("TA Test Tooltip Opened", {
    source: "Job/Log/Output/Line",
    organization_slug: organizationSlug,
  });
}

/**
 * Wrapper component that provides the log line selection context to the class component.
 */
export default function JobLogOutputLineWithSelection(props: Props) {
  const { selectLine, selectRange } = useLogLineSelection();

  return <JobLogOutputLine {...props} selectLine={selectLine} selectRange={selectRange} />;
}
